"use client";
import { ClipboardIcon } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { useLinkedInput } from "@/app/_components/ui/linked-input-provider";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  enableCopy?: boolean;
  formatAsMoney?: boolean;
  onEnterKeyPress?: () => void;
  linkId?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      enableCopy = false,
      formatAsMoney = false,
      onEnterKeyPress,
      linkId,
      ...props
    },
    ref,
  ) => {
    const internalRef = React.useRef<HTMLInputElement>(null);
    const linkedInputContext = useLinkedInput(linkId || "");
    const [displayValue, setDisplayValue] = React.useState(
      props.value?.toString() || "",
    );
    const [isFocused, setIsFocused] = React.useState(false);

    // Utility functions for money formatting
    const formatAsMoneyString = React.useCallback(
      (value: string, isFullFormat: boolean = false): string => {
        if (!formatAsMoney) return value;

        // Remove all non-digit and non-decimal characters
        let cleaned = value.replace(/[^0-9.]/g, "");

        // Handle multiple decimal points - keep only the first one
        const parts = cleaned.split(".");
        if (parts.length > 2) {
          cleaned = parts[0] + "." + parts.slice(1).join("");
        }

        if (!cleaned) return "";

        // Allow intermediate states while typing (e.g., "2", "2.", "2.0", "2.5")
        if (!isFullFormat) {
          // Parse as float
          const numericValue = parseFloat(cleaned);
          if (isNaN(numericValue)) {
            if (cleaned === ".") return "$0.";
            return "";
          }

          // Allow trailing decimal point or incomplete decimal numbers
          if (cleaned.endsWith(".")) {
            return `$${cleaned}`;
          }

          // Allow one decimal place (e.g., "2.5" or "2.0")
          if (/\.\d{1}$/.test(cleaned)) {
            return `$${cleaned}`;
          }

          // For whole numbers while typing, just add dollar sign without forcing decimals
          if (!cleaned.includes(".")) {
            return `$${cleaned}`;
          }

          // For complete decimal values (e.g., "2.50"), format properly
          return `$${numericValue.toFixed(2)}`;
        }

        // Parse as float for full formatting
        const numericValue = parseFloat(cleaned);
        if (isNaN(numericValue)) return "";

        // Format with 2 decimal places and thousands separators
        const formatted = numericValue.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        return `$${formatted}`;
      },
      [formatAsMoney],
    );

    const unformatMoneyString = React.useCallback(
      (value: string): string => {
        if (!formatAsMoney) return value;
        // Remove dollar sign and commas, keep digits and decimal point
        const cleaned = value.replace(/[$,]/g, "");
        // Return the cleaned value as-is to preserve intermediate states
        // like "2", "2.", "2.0" while typing
        if (cleaned === "" || cleaned === ".") return "";
        return cleaned;
      },
      [formatAsMoney],
    );

    // Update display value when props.value changes (for controlled components)
    React.useEffect(() => {
      if (formatAsMoney && props.value !== undefined && !isFocused) {
        setDisplayValue(formatAsMoneyString(props.value.toString(), true));
      } else if (props.value !== undefined && !isFocused) {
        setDisplayValue(props.value.toString());
      }
    }, [props.value, formatAsMoney, formatAsMoneyString, isFocused]);

    // Handle input changes
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      if (formatAsMoney) {
        // Allow partial formatting during typing
        const formatted = formatAsMoneyString(inputValue, false);
        setDisplayValue(formatted);

        // Create a synthetic event with the unformatted value for the parent
        const unformattedValue = unformatMoneyString(inputValue);
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: unformattedValue,
          },
        };

        if (props.onChange) {
          props.onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
        }
      } else {
        setDisplayValue(inputValue);
        if (props.onChange) {
          props.onChange(e);
        }
      }
    };

    // Handle blur to fully format the value
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);

      if (formatAsMoney) {
        const formatted = formatAsMoneyString(displayValue, true);
        setDisplayValue(formatted);
      }

      if (props.onBlur) {
        props.onBlur(e);
      }
    };

    // Handle focus
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);

      if (props.onFocus) {
        props.onFocus(e);
      }
    };

    // Merge the internal ref with the forwarded ref
    const mergedRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        internalRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    // Register with LinkedInputProvider if linkId is provided
    React.useEffect(() => {
      if (linkId && linkedInputContext && internalRef.current) {
        linkedInputContext.registerInput(linkId, internalRef.current);
        return () => {
          linkedInputContext.unregisterInput(linkId);
        };
      }
    }, [linkId, linkedInputContext]);

    const handleCopy = async () => {
      const inputValue = internalRef.current?.value;
      if (inputValue) {
        try {
          await navigator.clipboard.writeText(inputValue);
          toast("✅ Copied to clipboard", {
            className: "w-fit right-0",
          });
        } catch {
          toast.error("Unable to copy to clipboard");
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        // If we have an explicit onEnterKeyPress, use that
        if (onEnterKeyPress) {
          e.preventDefault();
          onEnterKeyPress();
        }
        // Otherwise, if we have a linkId and context, use auto-navigation
        else if (linkId && linkedInputContext) {
          e.preventDefault();
          linkedInputContext.focusNext(linkId);
        }
      }
      // Call the original onKeyDown if it exists
      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
    };

    if (enableCopy) {
      return (
        <div className="relative">
          <input
            type={type}
            className={cn(
              "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
              className,
            )}
            ref={mergedRef}
            {...props}
            value={formatAsMoney ? displayValue : props.value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onFocus={handleFocus}
          />
          <button
            type="button"
            onClick={handleCopy}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ClipboardIcon size={16} />
          </button>
        </div>
      );
    }

    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        ref={mergedRef}
        {...props}
        value={formatAsMoney ? displayValue : props.value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
